import "./styles.css";
import { GargantuaRenderer } from "./renderer";
import { applyUrlState, createDefaultState, loadState, QUALITY_PROFILES, saveState, sanitizeState, type AppState, type Parameters, type QualityName } from "./state";
import { createUi } from "./ui";

declare global {
  interface Window {
    __GARGANTUA_READY__: boolean;
    gargantua: {
      getState(): Readonly<AppState>;
      setState(patch: Partial<AppState>): void;
      capture(options?: { width?: number; height?: number; time?: number; download?: boolean }): Promise<Blob>;
      reset(): void;
    };
  }
}

const root = document.querySelector<HTMLElement>("#app");
if (!root) throw new Error("Application root not found.");
window.__GARGANTUA_READY__ = false;

const canvas = document.createElement("canvas");
canvas.id = "gargantua-canvas"; canvas.setAttribute("aria-label", "Realtime Schwarzschild black hole raytracer");
root.append(canvas);
const fallback = document.createElement("div"); fallback.className = "fallback"; fallback.hidden = true; root.append(fallback);

let state = applyUrlState(loadState());
let renderer: GargantuaRenderer;
let ui: ReturnType<typeof createUi>;
let animationTime = Number(new URLSearchParams(location.search).get("time")) || 0;
let lastFrame = performance.now();
let fps = 60;
let frameCounter = 0;
let performanceWindowStarted = performance.now();
let persistTimer = 0;
let readinessCheckStarted = false;
const captureMode = new URLSearchParams(location.search).get("capture") === "1";

const audio = new Audio("/audio/deep-space-ambience.ogg");
audio.loop = true; audio.preload = "auto"; audio.volume = state.audioVolume;

function persist(): void {
  clearTimeout(persistTimer);
  persistTimer = window.setTimeout(() => saveState(state), 160);
}

function setState(patch: Partial<AppState>): void {
  state = sanitizeState({ ...state, ...patch, parameters: { ...state.parameters, ...(patch.parameters ?? {}) } }, state);
  renderer?.setQuality(state.quality);
  renderer?.updateParameters(state.parameters);
  renderer?.setDebugView(state.debugView);
  renderer?.setSeed(state.seed);
  document.body.classList.toggle("hud-hidden", !state.hudVisible);
  syncAudio(); persist(); updateUi();
}

function updateParameter(key: keyof Parameters, value: number): void {
  const parameters = { ...state.parameters, [key]: value };
  if (key === "diskInnerRadius" && parameters.diskOuterRadius <= value + 1) parameters.diskOuterRadius = value + 1;
  state = sanitizeState({ ...state, parameters }, state);
  renderer.updateParameters(state.parameters); persist(); updateUi();
}

function setQuality(quality: QualityName): void {
  const profile = QUALITY_PROFILES[quality];
  state = sanitizeState({ ...state, quality, parameters: { ...state.parameters, maximumSteps: profile.maximumSteps } }, state);
  renderer.setQuality(quality); renderer.updateParameters(state.parameters); persist(); updateUi();
}

function cycleQuality(): void {
  const order: QualityName[] = ["standard", "high", "cinematic"];
  setQuality(order[(order.indexOf(state.quality) + 1) % order.length]!);
}

function applyPreset(name: string, immediate = false): void {
  state = { ...state, preset: name, cinematic: false };
  renderer.applyPreset(name, immediate); persist(); updateUi();
}

async function syncAudio(): Promise<void> {
  audio.volume = state.audioVolume;
  try {
    if (state.audioEnabled && !document.hidden) await audio.play(); else audio.pause();
  } catch {
    if (state.audioEnabled) state = { ...state, audioEnabled: false };
  }
}

async function saveCapture(options: { width?: number; height?: number; time?: number; download?: boolean } = {}): Promise<Blob> {
  const width = options.width ?? Math.round(innerWidth * Math.min(devicePixelRatio, 2));
  const height = options.height ?? Math.round(innerHeight * Math.min(devicePixelRatio, 2));
  const blob = await renderer.capture(width, height, options.time ?? animationTime);
  if (options.download === true) {
    const url = URL.createObjectURL(blob); const anchor = document.createElement("a");
    anchor.href = url; anchor.download = `gargantua-${width}x${height}.png`; anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return blob;
}

function reset(): void {
  state = applyUrlState(createDefaultState());
  renderer.setQuality(state.quality); renderer.updateParameters(state.parameters); renderer.setDebugView(state.debugView); renderer.setSeed(state.seed);
  renderer.applyPreset(state.preset, true); syncAudio(); saveState(state); updateUi();
}

function updateUi(): void {
  if (!ui || !renderer) return;
  const stats = renderer.getStats();
  const status = `${fps.toFixed(0)} FPS · ${stats.width}×${stats.height} · ${(stats.renderScale * 100).toFixed(0)}% · ${stats.hdr ? "HDR16F" : "LDR fallback"} · r=${renderer.getDistance().toFixed(1)}M`;
  ui.update(state, status);
}

function handleAction(action: string): void {
  if (action === "panel") ui.setPanelVisible(!document.querySelector(".control-panel")?.classList.contains("open"));
  else if (action === "help") ui.showHelp();
  else if (action === "capture") void saveCapture({ download: true });
  else if (action === "cinematic") setState({ cinematic: !state.cinematic });
  else if (action === "audio") setState({ audioEnabled: !state.audioEnabled });
  else if (action === "pause") setState({ paused: !state.paused });
  else if (action === "fullscreen") void document.documentElement.requestFullscreen?.();
  else if (action === "reset") reset();
}

try {
  renderer = new GargantuaRenderer(canvas, state);
  ui = createUi(root, state, {
    onParameter: updateParameter, onQuality: setQuality, onPreset: applyPreset,
    onDebug(view) { state = { ...state, debugView: view }; renderer.setDebugView(view); persist(); updateUi(); },
    onAudioVolume(value) { setState({ audioVolume: value }); },
    onAction: handleAction
  });
  renderer.onInteraction = () => { state = { ...state, cinematic: false, parameters: { ...state.parameters, cameraDistance: renderer.getDistance() } }; persist(); updateUi(); };
  renderer.onContextState = (message) => ui.showError(message);
  renderer.onShaderError = (message) => ui.showError(`${message} The diagnostic fallback remains available; inspect the browser console for the driver log.`);
  renderer.applyPreset(state.preset, true);
  renderer.resize(innerWidth, innerHeight);
} catch (error) {
  fallback.hidden = false;
  fallback.innerHTML = `<strong>WebGL 2 unavailable</strong><p>${error instanceof Error ? error.message : String(error)}</p><p>Use a current browser with hardware acceleration enabled.</p>`;
  throw error;
}

function waitForFrames(count: number): Promise<void> {
  return new Promise((resolve) => {
    const next = () => {
      count -= 1;
      if (count <= 0) resolve(); else requestAnimationFrame(next);
    };
    requestAnimationFrame(next);
  });
}

async function confirmRendererReady(): Promise<void> {
  let visible = await renderer.isOutputVisible(animationTime);
  if (!visible) {
    ui.showError("The renderer produced an unexpectedly dark validation frame. Recovering the Observer preset before declaring readiness.");
    applyPreset("observer", true);
    state.parameters.exposure = Math.max(state.parameters.exposure, 1.25);
    renderer.updateParameters(state.parameters);
    await waitForFrames(3);
    visible = await renderer.isOutputVisible(animationTime);
  }
  if (!visible) {
    ui.showError("The renderer did not produce a visible frame after recovery. Automation readiness remains false; inspect WebGL diagnostics.");
    return;
  }
  window.__GARGANTUA_READY__ = true;
  document.documentElement.dataset.ready = "true";
}

function animate(now: number): void {
  requestAnimationFrame(animate);
  const delta = Math.min(0.1, (now - lastFrame) / 1000); lastFrame = now;
  if (!state.paused && !captureMode) animationTime += delta;
  renderer.render(animationTime, state.cinematic && !captureMode);
  const instantFps = delta > 0 ? 1 / delta : 60; fps += (instantFps - fps) * 0.08; frameCounter++;
  if (now - performanceWindowStarted > 3200 && !captureMode) {
    const profile = QUALITY_PROFILES[state.quality];
    if (fps < 28) renderer.setRenderScale(renderer.getRenderScale() - 0.1);
    else if (fps > 52 && renderer.getRenderScale() < profile.scale) renderer.setRenderScale(renderer.getRenderScale() + 0.05);
    performanceWindowStarted = now;
  }
  if (frameCounter % 12 === 0) updateUi();
  if (frameCounter >= 2 && !readinessCheckStarted) {
    readinessCheckStarted = true;
    void confirmRendererReady();
  }
}
requestAnimationFrame(animate);

addEventListener("resize", () => renderer.resize(innerWidth, innerHeight));
document.addEventListener("visibilitychange", () => void syncAudio());

document.addEventListener("keydown", (event) => {
  const target = event.target as HTMLElement | null;
  if (target?.matches("input,textarea,select,[contenteditable=true]")) return;
  if (/^[0-9]$/.test(event.key) && !event.shiftKey) {
    state = { ...state, debugView: Number(event.key) }; renderer.setDebugView(state.debugView); persist(); updateUi(); return;
  }
  if (event.shiftKey && /^[1-4]$/.test(event.key)) {
    const names = ["observer", "photon-ring", "polar", "cinematic-wide"]; applyPreset(names[Number(event.key) - 1]!); return;
  }
  const key = event.key.toLowerCase();
  if (key === "c") setState({ cinematic: !state.cinematic });
  else if (key === "h") setState({ hudVisible: !state.hudVisible });
  else if (key === "m") setState({ audioEnabled: !state.audioEnabled });
  else if (key === "q") cycleQuality();
  else if (event.code === "Space") { event.preventDefault(); setState({ paused: !state.paused }); }
  else if (key === "f") void document.documentElement.requestFullscreen?.();
  else if (key === "s") void saveCapture({ download: true });
  else if (key === "r") reset();
  else if (key === "?") ui.showHelp();
});

window.gargantua = {
  getState: () => structuredClone(state),
  setState,
  capture: saveCapture,
  reset
};

if (captureMode) {
  const params = new URLSearchParams(location.search);
  state = {
    ...state,
    cinematic: false,
    hudVisible: params.has("hud") ? params.get("hud") !== "0" : false
  };
  document.body.classList.toggle("hud-hidden", !state.hudVisible);
  updateUi();
  const width = Number(params.get("width")) || 1920; const height = Number(params.get("height")) || 1080;
  const trigger = async () => {
    while (!window.__GARGANTUA_READY__) await new Promise((resolve) => setTimeout(resolve, 50));
    const blob = await saveCapture({ width, height, time: animationTime, download: false });
    (window as any).__GARGANTUA_CAPTURE_BLOB__ = blob;
  };
  void trigger();
}
